import { useEffect, useState } from 'react'
import { fxSonarRing } from '../../assets/index.js'
import { useAnimation } from '../../hooks/useAnimation.js'
import { useAudio } from '../../hooks/useAudio.js'
import { CELL_SIZE } from '../Grid/Cell.js'

interface SonarPingFXProps {
  /** Center cell pixel position */
  x: number
  y: number
  /** Zone cell keys for the 3x3 area */
  zoneCellPositions: { x: number; y: number }[]
  count: number
  onDone: () => void
}

type Phase = 'emit' | 'zone' | 'count' | 'fade' | 'done'

/**
 * Sonar Ping animation: emit ring → zone highlight → count reveal → fade.
 * ART_GUIDE_v2 §2.4. Total ~1.4s.
 */
export function SonarPingFX({ x, y, zoneCellPositions, count, onDone }: SonarPingFXProps) {
  const { phaseTimer, reduceMotion } = useAnimation()
  const { play } = useAudio()
  const [phase, setPhase] = useState<Phase>('emit')

  useEffect(() => {
    if (reduceMotion) {
      play('sonar-ping', 'combat')
      onDone()
      return
    }

    // Emit (200ms) — play sonar-ping sound
    play('sonar-ping', 'combat')
    phaseTimer(200, () => {
      setPhase('zone')
      phaseTimer(400, () => {
        setPhase('count')
        // Play sonar-result when count numeral is revealed (per AUDIO.md §2)
        play('sonar-result', 'combat')
        phaseTimer(300, () => {
          setPhase('fade')
          phaseTimer(500, () => {
            setPhase('done')
            onDone()
          })
        })
      })
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === 'done') return null

  return (
    <>
      {/* Sonar ring emission */}
      {phase === 'emit' && (
        <img
          src={fxSonarRing}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            left: x,
            top: y,
            width: CELL_SIZE,
            height: CELL_SIZE,
            pointerEvents: 'none',
            zIndex: 15,
            animation: 'sonar-emit 200ms cubic-bezier(0.22,1,0.36,1) forwards',
          }}
        />
      )}

      {/* Zone highlight */}
      {(phase === 'zone' || phase === 'count') && zoneCellPositions.map((pos, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: pos.x,
            top: pos.y,
            width: CELL_SIZE,
            height: CELL_SIZE,
            background: 'rgba(61, 220, 151, 0.18)',
            pointerEvents: 'none',
            zIndex: 8,
            animation: `sonar-zone-in 100ms ease-out ${i === 0 ? '0ms' : '150ms'} forwards`,
            opacity: 0,
          }}
        />
      ))}

      {/* Fade out zone */}
      {phase === 'fade' && zoneCellPositions.map((pos, i) => (
        <div
          key={`fade-${i}`}
          style={{
            position: 'absolute',
            left: pos.x,
            top: pos.y,
            width: CELL_SIZE,
            height: CELL_SIZE,
            background: 'rgba(61, 220, 151, 0.18)',
            pointerEvents: 'none',
            zIndex: 8,
            opacity: 1,
            transition: 'opacity 500ms ease-out',
            ...(phase === 'fade' ? { opacity: 0 } : {}),
          }}
        />
      ))}

      {/* Count numeral */}
      {(phase === 'count' || phase === 'fade') && (
        <div
          style={{
            position: 'absolute',
            left: x,
            top: y,
            width: CELL_SIZE,
            height: CELL_SIZE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 16,
            animation: phase === 'count'
              ? 'sonar-count-in 300ms cubic-bezier(0.22,1,0.36,1) forwards'
              : undefined,
            opacity: phase === 'fade' ? 0 : undefined,
            transition: phase === 'fade' ? 'opacity 500ms ease-out' : undefined,
          }}
        >
          <span style={{
            fontSize: 32,
            fontWeight: 700,
            color: '#EAF2F8',
            textShadow: '0 0 4px #0B2545, 0 0 2px #0B2545',
          }}>
            {count}
          </span>
        </div>
      )}
    </>
  )
}
